import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Contract } from '../types/contract';
import { contractService } from '../services/contractService';

interface ContractContextType {
  contracts: Contract[];
  loading: boolean;
  refreshContracts: () => Promise<void>;
  updateContract: (id: string, updates: Partial<Contract>) => void;
  deleteContract: (id: string) => void;
}

const ContractContext = createContext<ContractContextType | undefined>(undefined);

export const useContracts = () => {
  const context = useContext(ContractContext);
  if (context === undefined) {
    throw new Error('useContracts must be used within a ContractProvider');
  }
  return context;
};

interface ContractProviderProps {
  children: ReactNode;
}

export const ContractProvider: React.FC<ContractProviderProps> = ({ children }) => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);

  const loadContracts = async () => {
    try {
      setLoading(true);
      const data = await contractService.getContracts();
      setContracts(data);
    } catch (error) {
      console.error('Erro ao carregar contratos:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshContracts = async () => {
    await loadContracts();
  };

  const updateContract = (id: string, updates: Partial<Contract>) => {
    setContracts(prev => prev.map(contract => 
      contract.id === id ? { ...contract, ...updates } : contract
    ));
  };

  const deleteContract = async (id: string) => {
    try {
      await contractService.deleteContract(id);
      setContracts(prev => prev.filter(contract => contract.id !== id));
    } catch (error) {
      console.error('Erro ao excluir contrato:', error);
      // Opcional: Adicionar feedback para o usuário aqui
    }
  };

  useEffect(() => {
    loadContracts();
  }, []);

  return (
    <ContractContext.Provider value={{
      contracts,
      loading,
      refreshContracts,
      updateContract,
      deleteContract
    }}>
      {children}
    </ContractContext.Provider>
  );
};
